<script>
import ConversationTimelineAPI from 'dashboard/api/teletel/conversationTimeline';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const CATEGORY_STYLES = {
  conversation: {
    icon: 'i-lucide-message-square-plus',
    color: 'bg-n-blue-3 text-n-blue-11',
  },
  activity: { icon: 'i-lucide-history', color: 'bg-n-slate-3 text-n-slate-11' },
  lifecycle: { icon: 'i-lucide-route', color: 'bg-n-teal-3 text-n-teal-11' },
  flow: {
    icon: 'i-lucide-git-branch',
    color: 'bg-n-violet-3 text-n-violet-11',
  },
  reminder: {
    icon: 'i-lucide-alarm-clock',
    color: 'bg-n-amber-3 text-n-amber-11',
  },
  sla: { icon: 'i-lucide-timer-off', color: 'bg-n-ruby-3 text-n-ruby-11' },
  supervisor: {
    icon: 'i-lucide-brain-circuit',
    color: 'bg-n-amber-3 text-n-amber-11',
  },
};

export default {
  components: { Spinner },
  props: {
    conversation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      events: [],
      isLoading: false,
      loadFailed: false,
      requestSequence: 0,
      reminderScope: null,
    };
  },
  watch: {
    'conversation.id': {
      immediate: true,
      handler() {
        this.loadTimeline();
      },
    },
  },
  methods: {
    async loadTimeline() {
      if (!this.conversation?.id) return;
      const sequence = this.requestSequence + 1;
      this.requestSequence = sequence;
      this.isLoading = true;
      this.loadFailed = false;
      try {
        const { data } = await ConversationTimelineAPI.getTimeline(
          this.conversation.id
        );
        if (sequence !== this.requestSequence) return;
        this.events = data.payload || [];
        this.reminderScope = data.meta?.reminder_scope || null;
      } catch {
        if (sequence !== this.requestSequence) return;
        this.events = [];
        this.loadFailed = true;
      } finally {
        if (sequence === this.requestSequence) this.isLoading = false;
      }
    },
    eventStyle(category) {
      return CATEGORY_STYLES[category] || CATEGORY_STYLES.activity;
    },
    formatTime(value) {
      if (!value) return '—';
      return new Intl.DateTimeFormat(this.$i18n?.locale || 'ar', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value));
    },
  },
};
</script>

<template>
  <section
    class="conversation-panel flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-n-surface-1"
    :aria-label="$t('TELETEL_REMINDERS.TIMELINE.ARIA_LABEL')"
  >
    <header
      class="sticky top-0 z-20 border-b border-n-weak bg-n-surface-1 px-5 py-4"
    >
      <div class="flex items-center justify-between gap-3">
        <div>
          <h2 class="m-0 text-sm font-semibold text-n-slate-12">
            {{ $t('TELETEL_REMINDERS.TIMELINE.TITLE') }}
          </h2>
        </div>
        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded-lg border border-n-slate-5 bg-n-surface-1 text-n-slate-11 shadow-sm hover:bg-n-alpha-2"
          :aria-label="$t('TELETEL_REMINDERS.TIMELINE.REFRESH')"
          :disabled="isLoading"
          @click="loadTimeline"
        >
          <span
            class="i-lucide-refresh-cw size-4"
            :class="{ 'animate-spin': isLoading }"
          />
        </button>
      </div>
      <p
        v-if="reminderScope === 'current_user_only'"
        class="mb-0 mt-2 text-xs text-n-slate-10"
      >
        {{ $t('TELETEL_REMINDERS.TIMELINE.PRIVATE_REMINDERS') }}
      </p>
    </header>

    <div v-if="isLoading" class="flex flex-1 items-center justify-center p-8">
      <Spinner class="text-n-brand" />
    </div>

    <div
      v-else-if="loadFailed"
      class="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center"
    >
      <span class="i-lucide-circle-alert size-7 text-n-ruby-10" />
      <p class="m-0 text-sm text-n-slate-11">
        {{ $t('TELETEL_REMINDERS.TIMELINE.LOAD_ERROR') }}
      </p>
      <button
        type="button"
        class="rounded-lg border border-n-slate-5 px-3 py-1.5 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
        @click="loadTimeline"
      >
        {{ $t('TELETEL_REMINDERS.TIMELINE.RETRY') }}
      </button>
    </div>

    <div
      v-else-if="!events.length"
      class="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center"
    >
      <span class="i-lucide-list-tree size-7 text-n-slate-9" />
      <p class="m-0 text-sm text-n-slate-11">
        {{ $t('TELETEL_REMINDERS.TIMELINE.EMPTY') }}
      </p>
    </div>

    <ol v-else class="m-0 flex list-none flex-col px-5 py-4">
      <li
        v-for="(event, index) in events"
        :key="event.id"
        class="relative flex gap-3 pb-5 last:pb-0"
      >
        <span
          v-if="index < events.length - 1"
          class="absolute start-[15px] top-8 h-[calc(100%-1rem)] w-px bg-n-weak"
        />
        <span
          class="relative z-10 flex size-8 flex-shrink-0 items-center justify-center rounded-lg"
          :class="eventStyle(event.category).color"
        >
          <span class="size-4" :class="eventStyle(event.category).icon" />
        </span>
        <div class="min-w-0 flex-1 pt-0.5">
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{ event.title }}
          </p>
          <p
            v-if="event.detail"
            class="mb-0 mt-1 break-words text-xs text-n-slate-10"
          >
            {{ event.detail }}
          </p>
          <div
            class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-n-slate-10"
          >
            <time :datetime="event.occurred_at">{{
              formatTime(event.occurred_at)
            }}</time>
            <span v-if="event.actor">{{ event.actor }}</span>
          </div>
        </div>
      </li>
    </ol>
  </section>
</template>
